<template>
  <div>
    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-[#0000009e] flex items-center justify-center z-50" @click="closeModal">
        <div
          :class="[
            'bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-all duration-300 ease-in-out',
            showResultsList ? 'min-h-[60vh]' : 'min-h-[220px]'
          ]"
          style="transition-property: min-height, height, max-height;" @click.stop>
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold">
              {{ mode === 'create' ? 'Nouvel emplacement' : 'Modifier l\'emplacement' }}
            </h2>
            <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
              <XIcon class="h-6 w-6" />
            </button>
          </div>

          <!-- Recherche GeoNames / Champ Site -->
          <div class="mb-6">
            <div class="relative">
              <label class="block text-sm font-medium text-gray-700 mb-2 transition-colors duration-300 ease-in-out">
                {{ isManualEntry ? 'Nom du site' : '' }}
              </label>
              <div class="flex items-center gap-2">
                <div class="relative w-full">
                  <input v-model="searchQuery" type="text" class="w-full p-2 border border-gray-300 rounded-md pr-8"
                    :placeholder="isManualEntry ? 'Nom du site' : 'Rechercher un site, monument, lieu...'"
                    @input="!isManualEntry && handleSearch()" />
                  <!-- Croix pour reset -->
                  <button v-if="searchQuery && !isManualEntry" class="absolute inset-y-0 right-0 pr-2 flex items-center text-red-600" tabindex="-1" @click="resetSearchQuery">
                    <XIcon class="h-4 w-4" />
                  </button>
                </div>
                <!-- Loader pendant la recherche -->
                <LoaderIcon v-if="isLoading" class="animate-spin h-5 w-5 text-blue-500" />
              </div>
              <p v-if="locationData.geoname_id" class="mt-1 text-xs text-gray-500">
                GeoName_id: {{ locationData.geoname_id }}
              </p>

              <!-- Résultats de recherche -->
              <div v-if="!isManualEntry && searchResults.length > 0"
                class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                <div v-for="result in searchResults" :key="result.geonameId"
                  class="p-3 hover:bg-gray-100 cursor-pointer" @click="selectResult(result)">
                  <div class="font-medium">{{ result.name }}</div>
                  <div class="text-sm text-gray-600">
                    {{ result.adminName1 }}, {{ result.countryName }}
                  </div>
                </div>
              </div>
              <div v-if="!isManualEntry"
                class="mt-2 p-3 border hover:bg-gray-100 rounded-md cursor-pointer text-center text-blue-600 flex items-center justify-center"
                @click="handleManualEntry">
                <span class="flex items-center">
                  <PencilIcon class="h-4 w-4 mr-2" />
                  Saisir manuellement les informations
                  <span v-if="!isLoading && searchQuery.length >= 3 && searchResults.length === 0" class="ml-2 text-xs text-gray-500">(Aucun résultat trouvé)</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Formulaire manuel -->
          <div v-if="isManualEntry" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Adresse</label>
              <input v-model="locationData.address"
                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Adresse" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Code postal</label>
                <input v-model="locationData.post_code"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Code postal" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Ville</label>
                <input v-model="locationData.place"
                  class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Ville" />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Pays</label>
              <input v-model="locationData.country"
                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Pays" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Latitude</label>
                <div class="relative">
                  <input v-model.number="locationData.latitude" type="number" step="any"
                    class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Latitude" />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Longitude</label>
                <div class="relative">
                  <input v-model.number="locationData.longitude" type="number" step="any"
                    class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Longitude" />
                </div>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Lien</label>
              <input v-model="locationData.location_link"
                class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="https://..." />
            </div>
            
            <!-- Champ Confidentialité -->
            <div class="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <input
                id="is_confidential"
                v-model="locationData.is_confidential"
                type="checkbox"
                class="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                @change="handleConfidentialChange"
              />
              <label for="is_confidential" class="text-sm font-medium text-yellow-800">
                Marquer comme confidentiel
              </label>
            </div>
            <p class="text-xs text-yellow-600 mt-1">
              Une location confidentielle sera obfusquée dans les exports et ne sera visible que par vous et les administrateurs.
            </p>
            
            <!-- Champ Public/Privé -->
            <div class="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-md" :class="{ 'opacity-50': locationData.is_confidential }">
              <input
                id="is_public"
                v-model="locationData.is_public"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                :disabled="locationData.is_confidential"
              />
              <label for="is_public" class="text-sm font-medium text-blue-800" :class="{ 'text-gray-500': locationData.is_confidential }">
                {{ locationData.is_confidential ? 'Privé (confidentiel)' : 'Rendre public' }}
              </label>
            </div>
            <p class="text-xs text-blue-600 mt-1" :class="{ 'text-gray-500': locationData.is_confidential }">
              {{ locationData.is_confidential ? 'Une location confidentielle est automatiquement privée.' : 'Une location publique sera visible par tous les utilisateurs de la plateforme.' }}
            </p>
            <div v-if="showGeocodeButton" class="flex justify-end">
              <button class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                @click="handleGeocode">
                <RefreshCcwIcon class="h-4 w-4 mr-2" />
                Rechercher les coordonnées
              </button>
            </div>
          </div>

              <!-- Navigation -->
    <div class="flex justify-between mt-6">
      <button v-if="isManualEntry" class="text-gray-500 hover:text-gray-700 text-sm flex items-center"
        @click="backToSearch">
        <RotateCcwIcon class="h-4 w-4 mr-1" />
        Réinitialiser
      </button>
      <div class="ml-auto">
        <button class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600" :disabled="!canSubmit"
          @click="handleSubmit">
          {{ mode === 'create' ? 'Créer l\'emplacement' : 'Mettre à jour l\'emplacement' }}
        </button>
      </div>
    </div>
  </div>
</div>
</Teleport>

    <!-- Modal de confirmation -->
    <ModalAreYouSure
      v-model="showConfirmModal"
      title="Modifications non sauvegardées"
      message="Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?"
      confirm-text="Fermer sans sauvegarder"
      cancel-text="Continuer l'édition"
      @confirm="confirmClose"
      @cancel="cancelClose"
    />

    <!-- Notification -->
    <Notification ref="notification" />
  </div>
</template>

<script lang="ts">
import { Loader2 as LoaderIcon, PencilIcon, RefreshCcwIcon, RotateCcwIcon, XIcon } from 'lucide-vue-next';
import * as Nominatim from 'nominatim-browser';
import type { PropType } from 'vue';
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { createLocation, updateLocation } from '../../api/locations';
import type { Location, LocationCreate } from '../../api/types/entities/location/types';
import type { GeonamesResult } from '../../services/geonamesService';
import { searchGeonames } from '../../services/geonamesService';
import type { NotificationType } from '../../types/notification';
import Notification from '../Notification.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

export default defineComponent({
  name: 'LocationCreator',
  components: {
    XIcon,
    RefreshCcwIcon,
    PencilIcon,
    RotateCcwIcon,
    LoaderIcon, // Ajout du loader
    Notification,
    ModalAreYouSure
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    mode: {
      type: String as () => 'create' | 'edit',
      required: true
    },
    initialData: {
      type: Object as PropType<Location | undefined>,
      required: false,
      default: undefined
    },
    userId: {
      type: String,
      required: true
    },
    displayType: {
      type: String,
      default: 'card',
      validator: (value: string) => ['card', 'button'].includes(value)
    }
  },
  emits: ['location-created', 'location-updated', 'update:show'],
  setup(props, { emit }) {
    const showModal = ref(props.show);
    const searchQuery = ref('');
    const searchResults = ref<GeonamesResult[]>([]);
    const searchTimeout = ref<number | null>(null);
    const isManualEntry = ref(false);
    // Ajout de l'état de chargement
    const isLoading = ref(false);
    const notification = ref<InstanceType<typeof Notification> | null>(null);
    const showConfirmModal = ref(false);
    const initialDataSnapshot = ref<LocationCreate | null>(null);

    const locationData = reactive<LocationCreate>({
      uuid: undefined,
      user_id: props.userId,
      is_public: true,
      is_confidential: false,
      is_deleted: false,
      site: '',
      address: '',
      post_code: '',
      place: '',
      latitude: undefined,
      longitude: undefined,
      geoname_id: '',
      country: '',
      location_link: '',
    });

    // Synchroniser searchQuery avec locationData.site
    watch(searchQuery, (newValue) => {
      locationData.site = newValue;
    });

    // Surveiller les changements de la prop show
    watch(() => props.show, (newValue) => {
      showModal.value = newValue;
      if (newValue && props.mode === 'edit') {
        isManualEntry.value = true;
      }
    });

    // Surveiller les changements de showModal
    watch(showModal, (newValue) => {
      emit('update:show', newValue);
    });

    // Initialiser les données si en mode édition
    watch(() => props.initialData, (newData) => {
      if (newData) {
        Object.assign(locationData, {
          uuid: newData.uuid,
          user_id: newData.user_id,
          is_public: newData.is_public,
          is_confidential: newData.is_confidential || false,
          is_deleted: newData.is_deleted,
          created_at: newData.created_at,
          updated_at: newData.updated_at,
          site: newData.site,
          address: newData.address || '',
          post_code: newData.post_code || '',
          place: newData.place || '',
          latitude: newData.latitude,
          longitude: newData.longitude,
          geoname_id: newData.geoname_id || '',
          country: newData.country || '',
          location_link: newData.location_link || ''
        });
        searchQuery.value = newData.site;
        // Forcer l'affichage manuel du formulaire
        isManualEntry.value = true;
      }
    }, { immediate: true });

    // Watcher pour forcer la cohérence confidentiel/privé
    watch(() => locationData.is_confidential, (newValue) => {
      if (newValue) {
        locationData.is_public = false;
      }
    });

    // Ajout : watcher pour pré-remplir à chaque ouverture de la modale avec les données d'import, quel que soit le mode
    watch([
      () => props.initialData,
      showModal
    ], ([newData, modalVisible]) => {
      if (modalVisible && newData) {
        console.log('[LocationCreator] Pré-remplissage avec initialData:', JSON.parse(JSON.stringify(newData)));
        Object.assign(locationData, {
          user_id: newData.user_id || props.userId,
          uuid: newData.uuid || undefined, // Récupérer l'UUID s'il est fourni, sinon undefined
          site: newData.site || '',
          address: newData.address || '',
          post_code: newData.post_code || '',
          place: newData.place || '',
          latitude: newData.latitude,
          longitude: newData.longitude,
          geoname_id: newData.geoname_id || '',
          location_link: newData.location_link || '',
          is_public: newData.is_public ?? true,
          is_confidential: newData.is_confidential || false
        });
        // Si on a des données, passer en mode manuel
        if (newData.site || newData.address) {
          isManualEntry.value = true;
          searchQuery.value = newData.site || newData.address || '';
        }
        // Sauvegarder un snapshot des données initiales pour la comparaison
        initialDataSnapshot.value = JSON.parse(JSON.stringify(locationData));
        console.log('[LocationCreator] Formulaire après assignation :', JSON.parse(JSON.stringify(locationData)));
      } else {
        console.log('[LocationCreator] Watcher déclenché mais pas de données à appliquer (modalVisible =', modalVisible, ', initialData =', newData, ')');
        // Réinitialiser le snapshot quand le modal se ferme
        if (!modalVisible) {
          initialDataSnapshot.value = null;
        }
      }
    }, { immediate: true });

    const resetForm = (): void => {
      searchQuery.value = '';
      searchResults.value = [];
      isManualEntry.value = false;
      Object.assign(locationData, {
        uuid: undefined,
        user_id: props.userId,
        is_public: true,
        is_deleted: false,
        site: '',
        address: '',
        post_code: '',
        place: '',
        latitude: undefined,
        longitude: undefined,
        geoname_id: '',
        country: '',
        location_link: ''
      });
      // Réinitialiser le snapshot
      initialDataSnapshot.value = null;
    };

    // Fonction pour vérifier s'il y a des modifications
    const hasModifications = (): boolean => {
      if (!initialDataSnapshot.value) {
        // En mode création, vérifier si des champs ont été remplis
        return !!(locationData.site || locationData.address || locationData.post_code || 
                 locationData.place || locationData.country || locationData.latitude || 
                 locationData.longitude || locationData.location_link);
      }
      
      // En mode édition, comparer avec les données initiales
      const snapshot = initialDataSnapshot.value;
      return locationData.site !== snapshot.site ||
             locationData.address !== snapshot.address ||
             locationData.post_code !== snapshot.post_code ||
             locationData.place !== snapshot.place ||
             locationData.country !== snapshot.country ||
             locationData.latitude !== snapshot.latitude ||
             locationData.longitude !== snapshot.longitude ||
             locationData.location_link !== snapshot.location_link ||
             locationData.is_public !== snapshot.is_public ||
             locationData.is_confidential !== snapshot.is_confidential;
    };

    const closeModal = (): void => {
      if (hasModifications()) {
        showConfirmModal.value = true;
      } else {
        resetForm();
        showModal.value = false;
      }
    };

    const confirmClose = (): void => {
      showConfirmModal.value = false;
      resetForm();
      showModal.value = false;
    };

    const cancelClose = (): void => {
      showConfirmModal.value = false;
    };

    const handleSearch = (): void => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTimeout.value = window.setTimeout(async () => {
        if (searchQuery.value.length >= 3) {
          try {
            isLoading.value = true;
            const results = await searchGeonames(searchQuery.value);
            searchResults.value = results;
          } catch (error) {
            console.error('Erreur lors de la recherche:', error);
            searchResults.value = [];
          } finally {
            isLoading.value = false;
          }
        } else {
          searchResults.value = [];
        }
      }, 300);
    };

    const selectResult = async (result: GeonamesResult): Promise<void> => {
      try {
        locationData.site = result.name;
        locationData.latitude = result.lat ? Number(result.lat) : undefined;
        locationData.longitude = result.lng ? Number(result.lng) : undefined;
        locationData.geoname_id = result.geonameId.toString();

        // Utiliser Nominatim pour récupérer les détails complets de l'adresse
        const reverseResult = await Nominatim.reverseGeocode({
          lat: result.lat,
          lon: result.lng,
          addressdetails: true
        });

        if (reverseResult.address) {
          locationData.address = [
            reverseResult.address.house_number || '',
            reverseResult.address.road || ''
          ].filter(Boolean).join(' ') || '';
          locationData.post_code = reverseResult.address.postcode || '';
          locationData.place = reverseResult.address.city ||
            reverseResult.address.town ||
            reverseResult.address.village || '';
          locationData.country = reverseResult.address.country || result.countryName || '';
        } else {
          // Fallback sur les données de Geonames
          locationData.place = result.adminName1 || '';
          locationData.country = result.countryName || '';
        }

        // Afficher les champs manuels et vider les résultats de recherche
        isManualEntry.value = true;
        searchResults.value = [];
      } catch (error) {
        console.error('Erreur lors de la récupération des détails:', error);
      }
    };

    const handleManualEntry = (): void => {
      isManualEntry.value = true;
      searchResults.value = [];
    };

    const backToSearch = (): void => {
      isManualEntry.value = false;
      resetForm();
    };

    const handleSubmit = async (): Promise<void> => {
      try {
        if (props.mode === 'create') {
          const response = await createLocation(locationData);
          emit('location-created', response);
          showNotification('Emplacement créé avec succès', 'success');
          resetForm();
          showModal.value = false;
        } else {
          const response = await updateLocation(props.initialData?.uuid || '', locationData);
          emit('location-updated', response);
          showNotification('Emplacement mis à jour avec succès', 'success');
          resetForm();
          showModal.value = false;
        }
      } catch (error) {
        console.error(`Erreur lors de ${props.mode === 'edit' ? 'la mise à jour' : 'la création'} du lieu:`, error);
        showNotification('Erreur lors de la création/mise à jour de l\'emplacement', 'error');
      }
    };

    const canSubmit = computed(() => {
      return locationData.site.trim().length > 0;
    });

    const showResultsList = computed(() => {
      return !isManualEntry.value && searchResults.value.length > 0;
    });

    const showGeocodeButton = computed(() => {
      return ((locationData.address && locationData.post_code) ||
        (locationData.address && locationData.place) ||
        (locationData.post_code && locationData.place));
    });

    const handleGeocode = async (): Promise<void> => {
      try {
        const searchQuery = [
          locationData.address,
          locationData.post_code,
          locationData.place,
          locationData.country
        ].filter(Boolean).join(', ');

        const result = await Nominatim.geocode({
          q: searchQuery,
          limit: 1,
          addressdetails: true
        });

        if (result && result.length > 0) {
          locationData.latitude = Math.trunc(Number(result[0].lat) * 100000) / 100000;
          locationData.longitude = Math.trunc(Number(result[0].lon) * 100000) / 100000;
        } else {
          showNotification("Aucune coordonnée trouvée pour cette adresse", "warning");
        }
      } catch (error) {
        console.error('Erreur lors de la géolocalisation:', error);
        showNotification("Erreur lors de la recherche des coordonnées", "error");
      }
    };

    const showNotification = (message: string, type: NotificationType): void => {
      if (notification.value) {
        notification.value.showNotification(message, type);
      }
    };

    const resetSearchQuery = (): void => {
      searchQuery.value = '';
      searchResults.value = [];
    };

    const handleConfidentialChange = (): void => {
      // Si on rend confidentiel, forcer à privé
      if (locationData.is_confidential) {
        locationData.is_public = false;
      }
    };

    return {
      showModal,
      searchQuery,
      searchResults,
      isManualEntry,
      locationData,
      resetForm,
      closeModal,
      handleSearch,
      selectResult,
      handleManualEntry,
      backToSearch,
      handleSubmit,
      canSubmit,
      showResultsList,
      showGeocodeButton,
      handleGeocode,
      isLoading, // expose le loader
      resetSearchQuery,
      handleConfidentialChange,
      notification,
      showConfirmModal,
      confirmClose,
      cancelClose
    };
  }
});
</script>

<style scoped>
/* Forcer les styles pour tous les inputs dans LocationCreator */
input[type="text"],
input[type="email"],
input[type="url"],
input[type="password"],
input[type="number"],
input:not([type]),
textarea,
select {
  margin-bottom: 8px !important;
  border: 1px solid #d1d5db !important;
  /* border-gray-300 */
  border-radius: 6px !important;
  /* rounded-md */
  padding: 8px 12px !important;
  /* px-3 py-2 */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  /* shadow-sm */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  background-color: white !important;
  width: 100% !important;
  display: block !important;
}

/* États focus */
input[type="text"]:focus,
input[type="email"]:focus,
input[type="url"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input:not([type]):focus,
textarea:focus,
select:focus {
  border-color: #3b82f6 !important;
  /* border-blue-500 */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  /* ring-blue-500 */
  outline: none !important;
}

/* États readonly/disabled */
input:readonly,
input:disabled,
textarea:readonly,
textarea:disabled,
select:readonly,
select:disabled {
  background-color: #f9fafb !important;
  /* bg-gray-50 */
  border-color: #d1d5db !important;
  /* border-gray-300 */
  cursor: not-allowed !important;
  opacity: 0.6 !important;
}

/* Labels */
label {
  margin-bottom: 4px !important;
  font-weight: 500 !important;
  color: #374151 !important;
  /* text-gray-700 */
  display: block !important;
}

/* Placeholders */
input::placeholder,
textarea::placeholder {
  color: #9ca3af !important;
  /* text-gray-400 */
  opacity: 1 !important;
}

/* Transition douce sur la hauteur du modal */
.min-h-\[220px\] {
  min-height: 220px;
}
.min-h-\[420px\] {
  min-height: 420px;
}
</style>